import React, { useState, useEffect } from 'react';

const ProductSelection = ({ onAddItem, transferItems = [] }) => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [allProducts, setAllProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12); // 12 products per page (6x2 grid)

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchProducts(selectedCategory);
        } else {
            setAllProducts([]);
        }
        setSearchTerm('');
        setCurrentPage(1); // Reset to first page when category changes
    }, [selectedCategory]);

    useEffect(() => {
        setCurrentPage(1); // Reset to first page when search term changes
    }, [searchTerm]);

    const fetchCategories = async () => {
        try {
            const response = await fetch('https://doticom.vn:3000/categories');
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchProducts = async (categoryCode) => {
        setLoading(true);
        try {
            const response = await fetch(`https://doticom.vn:3000/items?category=${categoryCode}`);
            const data = await response.json();
            setAllProducts(data);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter out products that are already in transfer list
    const availableProducts = allProducts.filter(product => 
        !transferItems.some(item => item.maSP === product.MaSP)
    );

    // Filter products by search term
    const filteredProducts = searchTerm.trim() 
        ? availableProducts.filter(product => 
            product.Ten_SP.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.MaSP.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : availableProducts;

    // Calculate pagination
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    const handleQuantityChange = (maSP, quantity) => {
        setQuantities(prev => ({ ...prev, [maSP]: quantity }));
    };

    const handleAddProduct = (product) => {
        const quantity = quantities[product.MaSP];
        if (!quantity || quantity <= 0) {
            alert('Vui lòng nhập số lượng hợp lệ');
            return;
        }
        onAddItem(product, quantity);
        setQuantities(prev => ({ ...prev, [product.MaSP]: '' }));
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to top of products section
        document.querySelector('.products-section')?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
        });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        
        // Calculate start and end page numbers
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        // Adjust start page if we're near the end
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        if (currentPage > 1) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="btn btn-outline-primary btn-sm"
                    style={{ marginRight: '4px' }}
                >&lt;
                    <i className="fas fa-chevron-left"></i>
                </button>
            );
        }

        // First page if not visible
        if (startPage > 1) {
            pages.push(
                <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className="btn btn-outline-primary btn-sm"
                    style={{ marginRight: '4px' }}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(
                    <span key="ellipsis1" style={{ margin: '0 8px', color: '#6c757d' }}>
                        ...
                    </span>
                );
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
                    style={{ marginRight: '4px' }}
                >
                    {i}
                </button>
            );
        }

        // Last page if not visible
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(
                    <span key="ellipsis2" style={{ margin: '0 8px', color: '#6c757d' }}>
                        ...
                    </span>
                );
            }
            pages.push(
                <button
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                    className="btn btn-outline-primary btn-sm"
                    style={{ marginRight: '4px' }}
                > 
                    {totalPages}
                </button>
            );
        }

        // Next button
        if (currentPage < totalPages) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="btn btn-outline-primary btn-sm"
                > &gt;
                    <i className="fas fa-chevron-right"></i>
                </button>
            );
        }

        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                marginTop: '24px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#6c757d', marginRight: '12px' }}>
                        Trang {currentPage} / {totalPages} ({filteredProducts.length} sản phẩm)
                    </span>
                    {pages}
                </div>
            </div>
        );
    };

    const selectedCategoryName = categories.find(cat => cat.MaNLSP === selectedCategory)?.TenLoaiSP || '';

    return (
        <div className="card">
            <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className="fas fa-box-open" style={{ fontSize: '20px', color: '#1976d2' }}></i>
                    <div>
                        <h3 className="mb-0">Chọn sản phẩm điều chuyển</h3>
                        <small className="text-muted">Chọn danh mục và tìm kiếm sản phẩm cần điều chuyển</small>
                    </div>
                </div>
            </div>
            
            <div className="card-body">
                {/* Category Selection */}
                <div className="form-group">
                    <label style={{ fontWeight: '600', marginBottom: '8px' }}>
                        <i className="fas fa-tags me-2"></i>
                        Danh mục sản phẩm
                    </label>
                    <select 
                        value={selectedCategory} 
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="form-control"
                        style={{ fontSize: '14px' }}
                    >
                        <option value="">-- Chọn danh mục sản phẩm --</option>
                        {categories.map(cat => (
                            <option key={cat.MaNLSP} value={cat.MaNLSP}>
                                {cat.TenLoaiSP}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Search Bar */}
                {selectedCategory && (
                    <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px' }}>
                            <i className="fas fa-search me-2"></i>
                            Tìm kiếm trong "{selectedCategoryName}"
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Nhập tên hoặc mã sản phẩm..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-control"
                                style={{ paddingLeft: '40px', fontSize: '14px' }}
                            />
                            <i className="fas fa-search" style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#6c757d',
                                fontSize: '14px'
                            }}></i>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        color: '#6c757d',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        padding: '4px'
                                    }}
                                    title="Xóa tìm kiếm"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner-border text-primary" style={{ marginBottom: '16px' }}></div>
                        <p className="text-muted">Đang tải sản phẩm...</p>
                    </div>
                )}

                {/* No Category Selected */}
                {!selectedCategory && !loading && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '60px 20px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderRadius: '12px',
                        border: '2px dashed #dee2e6'
                    }}>
                        <i className="fas fa-arrow-up" style={{ 
                            fontSize: '48px', 
                            color: '#adb5bd',
                            marginBottom: '16px'
                        }}></i>
                        <h4 style={{ color: '#6c757d', marginBottom: '8px' }}>Chọn danh mục để bắt đầu</h4>
                        <p className="text-muted" style={{ fontSize: '14px' }}>
                            Vui lòng chọn danh mục sản phẩm từ dropdown phía trên
                        </p>
                    </div>
                )}

                {/* Products Display */}
                {selectedCategory && !loading && (
                    <div className="products-section">
                        {/* Products Stats */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '16px',
                            padding: '12px 16px',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fas fa-cube text-primary"></i>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                    {searchTerm ? 'Kết quả tìm kiếm' : 'Sản phẩm có sẵn'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <span className="badge bg-primary" style={{ fontSize: '12px' }}>
                                    {filteredProducts.length} sản phẩm
                                </span>
                                {totalPages > 1 && (
                                    <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                        Trang {currentPage}/{totalPages}
                                    </span>
                                )}
                            </div>
                        </div>

                        {filteredProducts.length === 0 ? (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '40px 20px',
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <i className="fas fa-inbox" style={{ 
                                    fontSize: '36px', 
                                    color: '#adb5bd',
                                    marginBottom: '16px'
                                }}></i>
                                <h5 style={{ color: '#6c757d', marginBottom: '8px' }}>
                                    {searchTerm ? 'Không tìm thấy sản phẩm' : 'Không có sản phẩm'}
                                </h5>
                                <p className="text-muted" style={{ fontSize: '14px' }}>
                                    {searchTerm 
                                        ? 'Thử thay đổi từ khóa tìm kiếm'
                                        : 'Tất cả sản phẩm trong danh mục này đã được thêm vào danh sách điều chuyển'
                                    }
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Products Grid */}
                                <div style={{ minHeight: '400px' }}>
                                    <div className="row" style={{ margin: '0 -8px' }}>
                                        {currentProducts.map(product => (
                                            <div key={product.MaSP} className="col-md-6" style={{ padding: '0 8px', marginBottom: '16px' }}>
                                                <div className="card" style={{ 
                                                    border: '1px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    transition: 'all 0.3s ease',
                                                    height: '100%'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                                                    e.currentTarget.style.borderColor = '#1976d2';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                    e.currentTarget.style.borderColor = '#e9ecef';
                                                }}>
                                                    <div className="card-body" style={{ padding: '16px' }}>
                                                        <div style={{ marginBottom: '12px' }}>
                                                            <h6 style={{ 
                                                                margin: '0 0 6px 0',
                                                                fontWeight: '600',
                                                                fontSize: '15px',
                                                                color: '#212529',
                                                                lineHeight: '1.4'
                                                            }}>
                                                                {product.Ten_SP}
                                                            </h6>
                                                            <div style={{ 
                                                                display: 'flex', 
                                                                justifyContent: 'space-between',
                                                                fontSize: '13px',
                                                                color: '#6c757d',
                                                                marginBottom: '8px'
                                                            }}>
                                                                <span>
                                                                    <i className="fas fa-barcode me-1"></i>
                                                                    {product.MaSP}
                                                                </span>
                                                                <span>
                                                                    <i className="fas fa-ruler me-1"></i>
                                                                    {product.DVT_SP}
                                                                </span>
                                                            </div>
                                                            <div style={{ 
                                                                fontSize: '15px',
                                                                fontWeight: '600',
                                                                color: '#1976d2'
                                                            }}>
                                                                {product.DonGia?.toLocaleString()} VND
                                                            </div>
                                                        </div>
                                                        
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <input
                                                                type="number"
                                                                placeholder="Số lượng"
                                                                value={quantities[product.MaSP] || ''}
                                                                onChange={(e) => handleQuantityChange(product.MaSP, e.target.value)}
                                                                className="form-control form-control-sm"
                                                                style={{ 
                                                                    flex: 1,
                                                                    fontSize: '13px'
                                                                }}
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                            {quantities[product.MaSP] && quantities[product.MaSP] > 0 && (
                                                                <button 
                                                                    onClick={() => handleAddProduct(product)}
                                                                    className="btn btn-primary btn-sm"
                                                                    style={{ 
                                                                        padding: '6px 12px',
                                                                        fontSize: '13px',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    <i className="fas fa-plus me-1"></i>
                                                                    Thêm
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Pagination */}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductSelection;
